import { RecalculationNote } from './RecalculationNote'
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { createPortal } from 'react-dom'
import type { LucideIcon } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import type { DealDossierTab } from '@/store/dealDossierStore'
import { referenceDataService } from '@/services/referenceData'
import { formatAmount, formatDate, formatMultiple, formatPercentField, textOrDash } from './format'
import { editableValue, fieldValueError } from '@/services/dealFile'
import { isWritableField, referenceCategoryFor, slotKindOf, unwritableReason, type SlotKind } from './editableFields'
import { useDossierEdit } from './editContext'
import { ValueStateChip } from './valueStateChip'
import { ComputedTrigger, outputFormulaOf } from './computedInputs'

function requiredLabel(label: string, required: boolean) {
  return (
    <>
      {label}
      {required ? <span className="text-red-400/80"> *</span> : null}
    </>
  )
}

function formatSlotValue(value: unknown, kind: SlotKind, fallback: string, name: string): string {
  if (value === null || value === undefined || value === '') return fallback
  switch (kind) {
    case 'currency':
      return formatAmount(value)
    case 'percent':
      // By the field's column, not the number's size: a custom_fields floor
      // of 0.5 is 0.50%, only the native ltv column keeps a fraction.
      return formatPercentField(value, name)
    case 'number':
      return formatMultiple(value)
    case 'count': {
      // 274080 SF read as "274080": a stored count is grouped for reading.
      const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''))
      return Number.isFinite(n) && String(value).trim() !== '' ? n.toLocaleString('en-US') : textOrDash(value)
    }
    case 'date':
      return formatDate(value)
    default:
      return textOrDash(value)
  }
}

/**
 * A dossier panel shown at full size.
 *
 * Every dashboard on the dossier packs a dozen panels into one viewport, so
 * each one gets a slice of the height rather than the height its content needs:
 * a four-row financing summary showed three rows, a twelve-year projection
 * showed one year, and a thirty-two line sponsor statement showed ten. The
 * figures were all there and all unreachable.
 *
 * The panel renders the SAME children here rather than a second, fuller view of
 * the same data — a detail view built from its own copy of the model is a
 * second thing to keep true, and the first time it drifts the dossier is
 * showing two different answers to one question. `dossier-expanded` lets the
 * charts and scroll boxes inside grow to fill the room (see index.css).
 */
function PanelModal({
  title,
  children,
  onClose,
  landscape = false,
}: {
  title: string
  children: ReactNode
  onClose: () => void
  landscape?: boolean
}) {
  const dialogRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    dialogRef.current?.querySelector<HTMLButtonElement>('button')?.focus()
    return () => previous?.focus()
  }, [])
  // Escape closes THIS panel and nothing else. The dossier shell listens for
  // Escape on window too, to close the whole record — so a bubbling handler
  // here would dismiss the dossier out from under the reader the first time
  // they closed an expanded panel. Taking the key during the capture phase and
  // stopping it there means the shell's listener never sees this one.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        const items = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), select:not(:disabled), input:not(:disabled), a[href]') ?? [])
        const first = items[0], last = items[items.length - 1]
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
      }
      if (event.key !== 'Escape') return
      event.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/75 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div
        /* Sized to its content up to a cap, not a fixed 88vh: a nine-row
           financing summary in a full-height window is the same dead space
           the dashboard had, just larger. Tall content still gets the room
           and scrolls at the cap. */
        ref={dialogRef}
        className={`dossier-shell flex max-h-[88vh] w-full ${landscape ? 'h-[88vh] max-w-[1500px]' : 'max-w-6xl'} flex-col overflow-hidden rounded-lg border border-amber-400/20 bg-[#0b0b0b] shadow-2xl`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-amber-400/10 px-4 py-2.5">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-300/90">
            {title}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-stone-500 hover:text-stone-200"
          >
            <X size={14} />
          </button>
        </div>
        <div className="dossier-expanded min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
      </div>
    </div>, document.body
  )
}

/**
 * The expand affordance for a panel this module does not own.
 *
 * Four pages grew their own panel chrome — `Panel` on Parties and Journal,
 * `Surface` on Underwriting, `Card` on Documents — before `DossierPanel`
 * existed. Rewriting all four to use it would be a large, risky diff across
 * four of the biggest files in the app for no visual gain, so they borrow the
 * mechanism instead of the chrome. The modal, and therefore the behaviour, is
 * still defined once.
 */
export function usePanelExpansion(title: string) {
  const [expanded, setExpanded] = useState(false)
  return {
    expanded,
    /**
     * Wrap the panel's heading text: the heading IS the expand control. The
     * panels used to carry a separate arrow icon beside their action; a dozen
     * of them on one dashboard read as clutter that said nothing the heading
     * could not, so the icon is gone and the heading does the work.
     */
    trigger: (children: ReactNode, className = '') => (
      <ExpandTrigger title={title} onClick={() => setExpanded(true)} className={className}>
        {children}
      </ExpandTrigger>
    ),
    /** Drop at the end of the panel, given the same children it renders. */
    modal: (children: ReactNode) =>
      expanded ? (
        <PanelModal title={title} onClose={() => setExpanded(false)}>
          {children}
        </PanelModal>
      ) : null,
  }
}

/**
 * The panel heading as a button. No icon: the heading text itself opens the
 * panel at full size, which keeps the header to the title and the panel's own
 * action. `aria-label` stays "Expand <title>" so assistive tech names what
 * the control does, not just what the panel is called.
 */
function ExpandTrigger({
  title,
  onClick,
  className = '',
  children,
}: {
  title: string
  onClick: () => void
  className?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Expand ${title}`}
      title="Open at full size"
      className={`min-w-0 max-w-full truncate text-left transition-colors hover:text-amber-200 focus-visible:underline focus-visible:outline-none ${className}`}
    >
      {children}
    </button>
  )
}

const TILE_TITLE = 'font-mono text-[9px] font-semibold uppercase tracking-[0.18em] text-amber-300/90'

export function OverviewTile({
  title,
  action,
  onAction,
  children,
  className = '',
  expandable = true,
}: {
  title: string
  action?: string
  onAction?: () => void
  children: ReactNode
  className?: string
  /** Off only where a full-size view says nothing the tile does not. */
  expandable?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  return (
    <section
      className={`dossier-panel flex h-full min-h-0 flex-col overflow-hidden rounded-md border border-amber-400/15 bg-black/30 ${className}`}
    >
      <div className="flex h-7 shrink-0 items-center justify-between gap-2 border-b border-amber-400/10 px-2">
        {expandable ? (
          <ExpandTrigger title={title} onClick={() => setExpanded(true)} className={TILE_TITLE}>
            {title}
          </ExpandTrigger>
        ) : (
          <span className={`min-w-0 truncate ${TILE_TITLE}`}>{title}</span>
        )}
        {action && onAction ? (
          <button type="button" onClick={onAction} className="shrink-0 text-[10px] text-amber-300 hover:text-amber-200">
            {action}
          </button>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 overflow-hidden p-1">{children}</div>
      {expanded ? (
        <PanelModal title={title} onClose={() => setExpanded(false)}>
          {children}
        </PanelModal>
      ) : null}
    </section>
  )
}

/** What a panel's children may want to know: whether they are drawn at full size. */
export type PanelContext = {
  expanded: boolean
  /** Opens the full-size view — for a slot that names what it cannot fit. */
  expand: () => void
  /** Closes the full-size view, for a child that navigates away from it (a no-op in the dashboard slot). */
  close: () => void
}

export function DossierPanel({
  title,
  action,
  onAction,
  children,
  className = '',
  dense = false,
  expandable = true,
  scroll = true,
  landscape = false,
  headerExtra,
}: {
  title: string
  action?: string
  /** Controls shown in the header before the action (e.g. a view toggle). */
  headerExtra?: ReactNode
  onAction?: () => void
  /** Static content, or a function of the panel context — so a panel can
   *  show more at full size (a map above a table) than in its dashboard slot. */
  children: ReactNode | ((ctx: PanelContext) => ReactNode)
  className?: string
  dense?: boolean
  /** Off only where a full-size view says nothing the panel does not. */
  expandable?: boolean
  /** Off where the dashboard slot must show its content whole: the content
   *  is sized to fit, and the full-size view is where the rest lives. */
  scroll?: boolean
  landscape?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const render = (ctx: PanelContext) => (typeof children === 'function' ? children(ctx) : children)
  return (
    <section
      className={`dossier-panel flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-amber-400/15 bg-black/30 ${className}`}
    >
      <div
        className={`flex shrink-0 items-center justify-between gap-2 border-b border-amber-400/10 px-3 ${
          dense ? 'py-1.5' : 'py-2'
        }`}
      >
        {expandable ? (
          <ExpandTrigger title={title} onClick={() => setExpanded(true)} className={TILE_TITLE}>
            {title}
          </ExpandTrigger>
        ) : (
          <span className={`min-w-0 truncate ${TILE_TITLE}`}>{title}</span>
        )}
        {headerExtra || (action && (onAction || expandable)) ? (
          <span className="flex shrink-0 items-center gap-2">
            {headerExtra}
            {action && (onAction || expandable) ? (
              <button type="button" onClick={onAction ?? (() => setExpanded(true))} className="shrink-0 text-[10px] text-amber-300 hover:text-amber-200">
                {action}
              </button>
            ) : null}
          </span>
        ) : null}
      </div>
      <div className={`flex min-h-0 flex-1 flex-col ${scroll ? 'overflow-y-auto' : 'overflow-hidden'} ${dense ? 'p-1.5' : 'p-3'}`}>
        {render({ expanded: false, expand: () => setExpanded(true), close: () => undefined })}
      </div>
      {expanded ? (
        <PanelModal title={title} landscape={landscape} onClose={() => setExpanded(false)}>
          {render({ expanded: true, expand: () => undefined, close: () => setExpanded(false) })}
        </PanelModal>
      ) : null}
    </section>
  )
}

export function MetricCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-md border border-amber-400/10 bg-black/40 px-3 py-2">
      <div className="font-mono text-[9px] font-semibold uppercase tracking-widest text-amber-300/90">{label}</div>
      <div className="truncate text-[15px] font-semibold text-stone-100">{value}</div>
    </div>
  )
}

export function DetailRow({ label, value, title }: { label: ReactNode; value: ReactNode; title?: string }) {
  return (
    <div className="flex items-start gap-2 text-[12px]" title={title}>
      <span className="w-[118px] shrink-0 text-stone-500">{label}</span>
      <span className="min-w-0 text-stone-200">{value}</span>
    </div>
  )
}

/**
 * Label over value, both allowed to wrap. `DetailRow` pins its label to a
 * 118px column so values line up down a panel; in a two-column summary that
 * column ate half of each cell and the values were cut off mid-figure.
 */
export function StackedField({
  label,
  value,
  derived = false,
  muted = false,
  note,
  title,
}: {
  label: ReactNode
  value: ReactNode
  derived?: boolean
  muted?: boolean
  note?: string
  title?: string
}) {
  const tone = muted ? 'text-stone-500' : derived ? 'text-violet-300' : 'text-stone-200'
  return (
    <div className="min-w-0" title={title}>
      <div className="font-mono text-[9px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className={`break-words text-[12px] leading-snug ${tone}`}>{value}</div>
      {note ? <div className="mt-0.5 text-[9px] text-stone-600">{note}</div> : null}
    </div>
  )
}

export function EmptyLine({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-[12px] text-stone-500">{children}</p>
}

/**
 * The write control for one slot.
 *
 * Free text everywhere except the governed vocabularies, which get the curated
 * list offered as completions. A `datalist` rather than a `select` on purpose:
 * these lists ship EMPTY — they are seeded from the client's verified list and
 * never invented — and the deal columns carry no foreign key, so a hard select
 * would make five real fields unfillable on any account whose lists have not
 * arrived yet. Offering the vocabulary is what actually shrinks the near-
 * duplicates; forbidding everything else would just block the desk.
 *
 * When a typed value is not in the list, that is stated rather than blocked: it
 * is the moment a new near-duplicate gets created, and the person typing is the
 * only one who can tell whether it is a genuine new entry.
 */
function SlotInput({
  name,
  label,
  kind,
  stored,
  onChange,
  fit = false,
  allowStage = false,
  multiline = false,
  placeholder = '—',
}: {
  name: string
  label: string
  kind: SlotKind
  stored: unknown
  onChange: (value: string) => void
  /** Shown while the box is empty: a reference figure, never a value. */
  placeholder?: string
  /** Size the box to its text rather than to the space around it — for a
   *  value that sits at the end of a row and takes only the room it needs. */
  fit?: boolean
  /** Offer the stage from the catalogue's own options, as the Audit tab does.
   *  Off by default: the slots that predate this keep the stage as text. */
  allowStage?: boolean
  /** Wrap a long value over the lines its read-only text already takes,
   *  rather than cut it at one line (nd-064: 'Value-Add Acquisiti'). Still one
   *  value: Enter does not add a line. */
  multiline?: boolean
}) {
  const category = referenceCategoryFor(name)
  const listId = useId()
  // Cached per category, shared across every slot governed by it. Curated
  // vocabularies change at the pace an admin edits them, not per render.
  const { data: options = [] } = useQuery({
    queryKey: ['reference-values', category],
    queryFn: () => referenceDataService.listValues(category as string),
    enabled: category !== null,
    staleTime: 5 * 60_000,
    // A desk user may not hold `reference_data:view`. The slot stays a plain
    // text box in that case rather than erroring over a completion list.
    retry: false,
  })

  const { defs } = useDossierEdit()
  const [typing, setTyping] = useState(false)
  const def = defs.find((d) => d.name === name)
  // The box shows the value the way it will be saved: an amount as
  // $130,329,000, a percent with its sign, a date as the day the read-only
  // dossier prints. What is being typed is left exactly as typed.
  const current = editableValue(def, stored, { typing })
  // A finished entry the record cannot hold says so on the box itself
  // ("abc" in Loan Amount, nd-062), not only when Save is refused.
  const entryError = !typing && def && typeof stored === 'string' ? fieldValueError(def, stored) : null
  // Free text that names no date stays a text box, so it is shown rather
  // than blanked by a date control that cannot hold it.
  const asDate = kind === 'date' && (current === '' || /^\d{4}-\d{2}-\d{2}$/.test(current))
  const governed = category !== null && options.length > 0
  const offList = governed && current.trim() !== '' && !options.includes(current)
  // In a monospace row one `ch` is one character, so the box is exactly as
  // wide as its text; a date reads MM/DD/YYYY, ten characters.
  const width = fit ? `calc(${asDate ? 10 : Math.max(current.length, 1)}ch + 6px)` : undefined

  // A catalogue `select` field is picked from its options: PUT /loans refuses
  // anything else (422), so a free-text box only taught the choices by
  // refusing a wrong one (nd-051). Stage keeps its own control (aliases, the
  // admin gate). A stored value outside the options stays visible, marked.
  const choices = category === null && (name !== 'stage' || allowStage) && def?.kind === 'select' ? def.options ?? [] : []
  if (choices.length > 0) {
    // The same value in another spelling (case, hyphens, spacing) IS the option: a deal saved as "SF"
    // or "Mixed-Use" before the client's lists (sf, Mixed Use) is shown on the option, not marked off it.
    const spelling = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
    const matched = choices.find((option) => spelling(option) === spelling(current))
    const offCatalogue = current.trim() !== '' && matched === undefined
    return (
      <select
        value={matched ?? current}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        className="dossier-edit-input"
        data-fit={fit ? '' : undefined}
      >
        <option value="">—</option>
        {offCatalogue ? <option value={current}>{`${current} (not a catalog option)`}</option> : null}
        {choices.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    )
  }

  // A wrapping box has no datalist, so a governed field offers its curated
  // list while it is being typed into (an <input>) and wraps otherwise.
  const wrap = multiline && !asDate && !(governed && typing)
  if (wrap) {
    // Laid over the value line (which holds the read-only text, invisibly,
    // and is positioned): the box takes exactly the lines the value takes
    // and adds nothing to the line's height.
    return (
      <span className="contents">
        <textarea
          value={current}
          rows={1}
          onChange={(e) => onChange(e.target.value.replace(/[\r\n]+/g, ' '))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault()
          }}
          onFocus={() => setTyping(true)}
          onBlur={() => setTyping(false)}
          placeholder={placeholder}
          aria-label={label}
          aria-invalid={entryError ? true : undefined}
          title={entryError ?? undefined}
          data-multiline=""
          className="dossier-edit-input"
          style={{ position: 'absolute', inset: '0 -3px', width: 'auto', height: 'auto', margin: 0, resize: 'none', overflow: 'hidden', overflowWrap: 'anywhere' }}
        />
        {entryError && (
          <span role="alert" className="pointer-events-none absolute left-0 top-full z-10 mt-0.5 whitespace-nowrap rounded bg-black/90 px-1 font-sans text-[9px] font-normal leading-tight text-red-300">
            {entryError}
          </span>
        )}
      </span>
    )
  }

  return (
    <span className={offList || entryError ? 'relative block' : 'contents'}>
      <input
        autoFocus={multiline && governed && typing}
        value={current}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setTyping(true)}
        onBlur={() => setTyping(false)}
        placeholder={placeholder}
        type={asDate ? 'date' : 'text'}
        inputMode={kind === 'text' || kind === 'date' || category ? undefined : 'decimal'}
        aria-label={label}
        aria-invalid={entryError ? true : undefined}
        title={entryError ?? undefined}
        list={governed ? listId : undefined}
        autoComplete={governed ? 'off' : undefined}
        className="dossier-edit-input"
        style={width ? { width } : undefined}
        data-fit={fit ? '' : undefined}
        // A fitted date has no picker icon (see dossier-theme.css); the
        // calendar opens on click instead.
        onClick={fit && asDate ? (e) => openDatePicker(e.currentTarget) : undefined}
      />
      {governed && (
        <datalist id={listId}>
          {options.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
      )}
      {entryError && (
        // Laid over what is below, like the curated-list notice: the message
        // must not move the dossier.
        <span role="alert" className="pointer-events-none absolute left-0 top-full z-10 mt-0.5 whitespace-nowrap rounded bg-black/90 px-1 font-sans text-[9px] font-normal leading-tight text-red-300">
          {entryError}
        </span>
      )}
      {!entryError && offList && (
        // Laid over what is below rather than pushing it down: a notice about
        // the value being typed must not move the dossier while it is typed.
        <span className="pointer-events-none absolute left-0 top-full z-10 mt-0.5 whitespace-nowrap rounded bg-black/90 px-1 font-sans text-[9px] font-normal leading-tight text-amber-300/80">
          Not in the curated list — saving adds a new one
        </span>
      )}
    </span>
  )
}

function openDatePicker(input: HTMLInputElement) {
  try {
    input.showPicker?.()
  } catch {
    // Refused outside a user gesture or in a cross-origin frame; the date can
    // still be typed.
  }
}

/** A slot's label, with the required marker the catalogue gives it. */
export function SlotLabel({ name, label }: { name: string; label: string }) {
  const { defs } = useDossierEdit()
  return requiredLabel(label, defs.some((d) => d.name === name && d.required))
}

/** The input for one slot, drawn where its value text is drawn in read mode. */
export function SlotValueInput({
  name,
  label,
  kind = 'text',
  fit = false,
  allowStage = false,
  multiline = false,
  entry,
}: {
  name: string
  label: string
  kind?: SlotKind
  fit?: boolean
  allowStage?: boolean
  multiline?: boolean
  /**
   * A box that does not show the field's current value: what it shows and
   * what typing does are the caller's. The Sponsor vs lender comparison uses
   * it for the client's column, which must start empty when the figure on the
   * deal is the package's — the same number in both columns read as agreement.
   * It still writes the dossier draft, so Save is the one save path.
   */
  entry?: { stored: unknown; onChange: (value: string) => void; placeholder?: string }
}) {
  const edit = useDossierEdit()
  return (
    <SlotInput
      name={name}
      label={label}
      kind={kind}
      stored={entry ? entry.stored : edit.effectiveValue(name)}
      onChange={entry ? entry.onChange : (value) => edit.setField(name, value)}
      fit={fit}
      allowStage={allowStage}
      multiline={multiline}
      placeholder={entry?.placeholder}
    />
  )
}

/** The catalogue input for a field, of the kind its catalogue entry says —
 *  what a derived value's inputs panel and a composite value's fields draw. */
export function CatalogueInput({ name, label }: { name: string; label: string }) {
  const { defs } = useDossierEdit()
  return <SlotValueInput name={name} label={label} kind={slotKindOf(defs.find((d) => d.name === name))} allowStage />
}

export const renderCatalogueInput = (name: string, label: string) => <CatalogueInput name={name} label={label} />


export function EditableSlot({
  name,
  label,
  caption,
  display,
  kind = 'text',
  layout = 'metric',
  icon: Icon,
  compact = false,
}: {
  name: string
  label: string
  /**
   * The line drawn above the value, when it is not the field's name — the
   * comparison captions a figure with its source ("Email L5", "Model C16").
   * `label` still names the input for assistive tech, and the catalogue's
   * required marker stays with the field's name, wherever the caller draws
   * that: on a source caption the red asterisk read as a flag on the email.
   */
  caption?: string
  display: string
  kind?: SlotKind
  /** `stack`: label above a wrapping value — for two-column summaries
   *  where a fixed label column would truncate every long value. */
  layout?: 'metric' | 'row' | 'header' | 'tile' | 'stack'
  /** Tile layout only — matches the icon-badge tiles used across the
   *  redesigned dossier dashboards, so unlocking one doesn't visually
   *  demote it to a plain unmarked box. */
  icon?: LucideIcon
  /** Tile layout only — a shorter tile for a panel that must hold six of
   *  them without scrolling. */
  compact?: boolean
}) {
  const edit = useDossierEdit()
  const writable = isWritableField(name, edit.defs)
  const stored = edit.effectiveValue(name)
  const formatted = formatSlotValue(stored, kind, display, name)
  const derived = edit.isDerivedField(name)
  // Unlocking swaps the value text for an input and changes nothing else:
  // every layout below draws the same box, label and value line in both
  // modes, and only the value line's content differs. A slot a save cannot
  // write reads exactly as it does locked, with the reason on hover.
  // R1 (nd-030): people resolve inputs, never outputs. A value the engine
  // derives (The Marlowe's cap rate, NOI / value) is not an input box when the
  // dossier unlocks; the figure stays where it is and opens its inputs, in
  // place, so the correction is made here rather than under Audit. A value
  // the server's preview has just recalculated from unsaved edits is the same
  // output a moment before it is stored, and reads the same way.
  const outputFormula = outputFormulaOf(edit, name)
  const figure =
    edit.unlocked && outputFormula !== null ? (
      <ComputedTrigger
        name={name}
        label={label}
        formula={outputFormula}
        renderInput={(input, inputLabel) => (
          <SlotValueInput name={input} label={inputLabel} kind={slotKindOf(edit.defs.find((d) => d.name === input))} />
        )}
      >
        {formatted}
      </ComputedTrigger>
    ) : (
      formatted
    )
  const liveDisplay = <>{figure}{derived && <RecalculationNote label={label} explanation={edit.derivation?.notes?.[name] ?? "Recalculated from dependent deal fields."} />}</>
  const marked = caption !== undefined ? <>{caption}</> : <SlotLabel name={name} label={label} />
  const tone = derived ? 'text-violet-300' : 'text-stone-100'
  const editing = edit.unlocked && writable && outputFormula === null
  const heldSource =
    !editing && !derived && stored !== null && stored !== undefined && stored !== ''
      ? edit.valueSource?.(name) ?? null
      : null
  const stateChip = heldSource?.state && heldSource.state !== 'stated' ? <ValueStateChip source={heldSource} /> : null
  // A wrapping value (row, stack) carries the chip after it, as the Overview
  // rows do. A one-line tile or header keeps the figure whole and puts the
  // chip beside its label, which truncates first: in a narrow tile the figure
  // is what must stay readable.
  const shownValue = stateChip
    ? <>{liveDisplay}<span className="ml-1 inline-block align-middle">{stateChip}</span></>
    : liveDisplay
  const labelLine = (className: string) =>
    stateChip && !editing ? (
      <span className={`flex min-w-0 items-center gap-1 ${className}`}>
        <span className="min-w-0 truncate" title={label}>{marked}</span>
        <span className="shrink-0">{stateChip}</span>
      </span>
    ) : (
      <span className={`block ${className}`}>{marked}</span>
    )
  const lockedReason = !edit.unlocked
    ? undefined
    : outputFormula !== null
      ? `Derived: ${outputFormula}. Change its inputs, not the result.`
      : !writable ? unwritableReason(name) : undefined
  const input = <SlotValueInput name={name} label={label} kind={kind} />

  if (layout === 'row') {
    if (!editing) return <DetailRow label={marked} value={shownValue} title={lockedReason} />
    return (
      <label className="flex items-start gap-2 text-[12px]">
        <span className="w-[118px] shrink-0 text-stone-500">{marked}</span>
        <span className="min-w-0 flex-1 text-stone-200">{input}</span>
      </label>
    )
  }

  if (layout === 'stack') {
    if (!editing) return <StackedField label={marked} value={shownValue} derived={derived} title={lockedReason} />
    return (
      <label className="block min-w-0">
        <span className="block font-mono text-[9px] uppercase tracking-widest text-stone-500">{marked}</span>
        <span className="block text-[12px] leading-snug text-stone-200">{input}</span>
      </label>
    )
  }

  if (layout === 'tile') {
    const Shell = editing ? 'label' : 'div'
    return (
      <Shell
        title={lockedReason}
        className={`flex h-full items-center gap-2 rounded-md border border-amber-400/10 bg-black/40 ${compact ? 'min-h-[36px] px-2 py-1' : 'min-h-[48px] px-2.5 py-2'}`}
      >
        {Icon ? (
          <span className={`flex shrink-0 items-center justify-center rounded border border-amber-300/10 bg-amber-300/[0.04] text-amber-300/70 ${compact ? 'h-6 w-6' : 'h-7 w-7'}`}>
            <Icon size={compact ? 11 : 13} />
          </span>
        ) : null}
        <span className="block min-w-0 flex-1">
          {labelLine('truncate font-mono text-[8px] uppercase tracking-widest text-stone-500')}
          <span className={`block ${editing ? '' : 'truncate'} whitespace-nowrap font-semibold leading-tight ${compact ? 'text-[12px]' : 'text-[13px]'} ${tone}`}>
            {editing ? input : liveDisplay}
          </span>
        </span>
      </Shell>
    )
  }

  if (layout === 'header') {
    const Shell = editing ? 'label' : 'div'
    return (
      <Shell className="block min-w-0" title={lockedReason}>
        {labelLine('font-mono text-[9px] uppercase tracking-widest text-stone-600')}
        <span className={`block ${editing ? '' : 'truncate'} whitespace-nowrap text-[13px] ${tone}`}>
          {editing ? input : liveDisplay}
        </span>
      </Shell>
    )
  }

  const Shell = editing ? 'label' : 'div'
  return (
    <Shell className="block min-w-0 rounded-md border border-amber-400/10 bg-black/40 px-3 py-2" title={lockedReason}>
      {labelLine('font-mono text-[9px] uppercase tracking-widest text-stone-500')}
      <span className={`block ${editing ? '' : 'truncate'} text-[15px] font-semibold ${tone}`}>
        {editing ? input : liveDisplay}
      </span>
    </Shell>
  )
}

export function gotoLabel(tab: DealDossierTab): string {
  switch (tab) {
    case 'overview':
      return 'Overview'
    case 'property':
      return 'Property'
    case 'economics':
      return 'Economics'
    case 'underwriting':
      return 'Underwriting'
    case 'capital_stack':
      return 'Capital Stack'
    case 'parties':
      return 'Parties'
    case 'documents':
      return 'Documents'
    case 'journal':
      return 'Journal'
    case 'audit':
      return 'Audit'
    case 'field_map':
      return 'Field Map'
    default: {
      const _n: never = tab
      return _n
    }
  }
}
